import axios, { AxiosError } from 'axios'
import type { AxiosInstance } from 'axios'

export interface HttpError<T = Record<string, Record<string, string>>> {
  isHttpError: true
  message: string | undefined
  status: number | undefined
  errors?: T
  raw: Error
}

export type HttpClient = AxiosInstance

export interface HttpClientOptions {
  baseURL: string
  headers?: Record<string, string>
}

function getCookieValue(searchKey: string) {
  let val: string | undefined = ''
  document.cookie.split(';').forEach((cookie) => {
    const [key, value] = cookie.split('=')
    if (key === searchKey) {
      return (val = value)
    }
  })

  return val
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeError = (error: AxiosError<any>): HttpError => ({
  isHttpError: true,
  status: error.response && error.response.status,
  message: error.response && error.response.data && error.response.data.message,
  errors: error.response && error.response.data && error.response.data.errors,
  raw: error,
})

export const isHttpError = (error: unknown): error is HttpError => {
  return (error as HttpError)?.isHttpError === true
}

const isSSR = () => typeof window === 'undefined'

export const createHttpClient = ({
  baseURL,
  headers = {},
}: HttpClientOptions): HttpClient => {
  const client = axios.create({
    baseURL,
    adapter: 'fetch',
    timeout: 15 * 1000,
    withCredentials: true,
    headers: {
      ...headers,
      'X-Client': 'gyym-sdk',
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(typeof window !== 'undefined' && {
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN'),
      }),
    },
  })

  if (process.env.NODE_ENV === 'development') {
    client.interceptors.request.use((req) => {
      console.log(
        `[GYYM Client] [${isSSR() ? 'Server' : 'Browser'}]`,
        `${req.baseURL}${req.url}`,
      )
      return req
    })
  }

  client.interceptors.response.use(
    (res) => res.data,
    (error) => Promise.reject(normalizeError(error)),
  )

  return client
}
